import { useMemo } from 'react';
import { InfiniteListBase, WithListContext, useTranslate } from 'react-admin';
import {
    AUDIT_METADATA_AVATAR_TYPE_NAME,
    AVATARS,
    DISORDER_METADATA_AVATAR_TYPE_NAME,
    TREATMENT_METADATA_AVATAR_TYPE_NAME,
} from '@/utils/CONST';
import Box from '@mui/material/Box';
import CollapseMenu from '@/components/common/collapse-menu/collapse-menu';
import { get, map, nth } from 'lodash';
import ListIcon from '@mui/icons-material/List';
import Typography from '@mui/material/Typography';
import useGetAvatar from '@/hooks/useGetAvatar';
import DisordersTreatmentExportItem from '@/components/interventions/export-intervention/disorders-treatment-export-item';

interface Props {
    /** @id o URI del avatar intervención (misma fuente que en export PDF) */
    selectedAvatarUriId: string;
    collapseMenuDisableInternalScroll?: boolean;
    collapseMenuDialogSection?: boolean;
    demandItemVariant?: 'export' | 'dialog';
}

/**
 * Misma lista y tarjetas que la sección disorders/treatment del PDF de intervención
 * (`ExportInterventionDisordersTreatmentSection`), para usar en agenda u otros diálogos.
 */
const InterventionsDemandsList = ({
    selectedAvatarUriId,
    collapseMenuDisableInternalScroll = false,
    collapseMenuDialogSection = false,
    demandItemVariant = 'export',
}: Props) => {
    const translate = useTranslate();

    const { avatar: intervention, isLoading: isLoadingIntervention } = useGetAvatar({
        avatarUriId: selectedAvatarUriId,
    });

    const interventionGroupAvatar: unknown = useMemo(
        () => nth(get(intervention, 'groupAvatars'), 0),
        [intervention],
    );

    return (
        <CollapseMenu
            icon={<ListIcon />}
            text={translate('intervention.list.demands')}
            disableInternalScroll={collapseMenuDisableInternalScroll}
            dialogSection={collapseMenuDialogSection}
        >
            <Box
                sx={
                    collapseMenuDialogSection
                        ? { p: 0, m: 0 }
                        : {
                              px: 1,
                              pb: 1,
                          }
                }
            >
                {isLoadingIntervention ? null : !interventionGroupAvatar ? (
                    <Typography
                        sx={{
                            py: collapseMenuDialogSection ? 0 : undefined,
                            pb: collapseMenuDialogSection ? 0 : 1,
                            textAlign: 'center',
                        }}
                    >
                        {translate('messages.no_items')}
                    </Typography>
                ) : (
                    <InfiniteListBase
                        resource={AVATARS}
                        filter={{
                            metadataAvatarTypeEName: [
                                TREATMENT_METADATA_AVATAR_TYPE_NAME,
                                DISORDER_METADATA_AVATAR_TYPE_NAME,
                                AUDIT_METADATA_AVATAR_TYPE_NAME,
                            ],
                            enabled: true,
                            'groupAvatars.id': interventionGroupAvatar,
                        }}
                        storeKey='interventionsDemandsDialogList'
                        sort={{
                            field: 'creationDate',
                            order: 'DESC',
                        }}
                        perPage={100}
                        disableSyncWithLocation
                        queryOptions={{
                            enabled: !!interventionGroupAvatar,
                        }}
                    >
                        <WithListContext
                            render={({ data, isPending }) => {
                                if (isPending) {
                                    return null;
                                }
                                const rows = data ? Object.values(data) : [];
                                if (rows.length === 0) {
                                    return (
                                        <Typography
                                            sx={{
                                                py: collapseMenuDialogSection ? 0 : undefined,
                                                pb: collapseMenuDialogSection ? 0 : 1,
                                                textAlign: 'center',
                                            }}
                                        >
                                            {translate('messages.no_items')}
                                        </Typography>
                                    );
                                }
                                return (
                                    <Box
                                        sx={{
                                            display: 'grid',
                                            gap: 2,
                                        }}
                                    >
                                        {map(rows, (item, index) => (
                                            <DisordersTreatmentExportItem
                                                key={item.id}
                                                item={item}
                                                index={index}
                                                variant={demandItemVariant}
                                            />
                                        ))}
                                    </Box>
                                );
                            }}
                        />
                    </InfiniteListBase>
                )}
            </Box>
        </CollapseMenu>
    );
};

export default InterventionsDemandsList;
